import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, TrendingUp, Brain, Target, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { ManualPredictionComparisonService } from '@/services/ManualPredictionComparisonService';
import { AnalysisMode, AnalysisConfig } from '@/types/trading-analysis-types';
import { TradingFile } from '@/services/TradingPatternAnalysisService';

interface ManualPredictionComparisonProps {
  tradingFiles: TradingFile[];
  selectedFile: TradingFile | null;
  onFileSelect: (file: TradingFile) => void;
  analysisConfig: AnalysisConfig & { analysisMode: AnalysisMode };
  hasPatternsForMode: boolean;
}

interface ComparisonResult {
  currentPrediction: any;
  honestBacktestPrediction: any;
  differences: {
    winProbabilityDiff: number;
    confidenceDiff: number;
    patternsCountDiff: number;
    predictionMatch: boolean;
  };
}

export const ManualPredictionComparison: React.FC<ManualPredictionComparisonProps> = ({
  tradingFiles,
  selectedFile,
  onFileSelect,
  analysisConfig,
  hasPatternsForMode
}) => {
  const [predictionSequence, setPredictionSequence] = useState('');
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);

  const handleComparison = async () => {
    if (!selectedFile?.id || !predictionSequence.trim()) {
      toast.error('Please select a file and enter a sequence');
      return;
    }

    const sequence = predictionSequence.trim();
    if (!/^[01,\s]+$/.test(sequence)) {
      toast.error('Sequence must contain only 0s, 1s, spaces, and commas');
      return;
    }

    setIsComparing(true);
    try {
      const result = await ManualPredictionComparisonService.comparepredictions(
        selectedFile.id,
        sequence,
        analysisConfig.analysisMode,
        analysisConfig
      );

      setComparisonResult(result);
      toast.success('Prediction comparison completed');
    } catch (error) {
      console.error('Comparison failed:', error);
      toast.error(error instanceof Error ? error.message : 'Comparison failed');
    } finally {
      setIsComparing(false);
    }
  };

  const getPredictionBadgeVariant = (prediction: string) => {
    switch (prediction) {
      case 'WIN': return 'default';
      case 'LOSS': return 'destructive';
      case 'SKIP':
      case 'UNCERTAIN': return 'secondary';
      default: return 'outline';
    }
  };

  const getConfidenceBadgeVariant = (confidence: number | string) => {
    const numConfidence = typeof confidence === 'string' ? 
      ({ 'very_high': 85, 'high': 70, 'medium': 55, 'low': 40, 'very_low': 25 }[confidence] || 25) : 
      confidence;
    
    if (numConfidence >= 70) return 'default';
    if (numConfidence >= 50) return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Manual Prediction Comparison
          </CardTitle>
          <CardDescription>
            Compare Current prediction logic vs Honest Backtest prediction logic
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="file-select">Select Data File</Label>
            <Select
              value={selectedFile?.id || ''}
              onValueChange={(value) => {
                const file = tradingFiles.find(f => f.id === value);
                if (file) onFileSelect(file);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a file for prediction" />
              </SelectTrigger>
              <SelectContent>
                {tradingFiles.map(file => (
                  <SelectItem key={file.id} value={file.id}>
                    {file.filename} ({file.total_trades} trades)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!hasPatternsForMode && selectedFile && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                No patterns available for {analysisConfig.analysisMode} mode. 
                Please run pattern analysis first or select a different mode.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="prediction-sequence">Input Sequence</Label>
            <Input
              id="prediction-sequence"
              placeholder="e.g., 1,0,1,1,0"
              value={predictionSequence}
              onChange={(e) => setPredictionSequence(e.target.value)}
              disabled={!selectedFile || !hasPatternsForMode}
            />
            <p className="text-xs text-muted-foreground">
              Enter comma-separated sequence of 1s (wins) and 0s (losses)
            </p>
          </div>

          <Button 
            onClick={handleComparison}
            disabled={isComparing || !selectedFile || !predictionSequence.trim() || !hasPatternsForMode}
            className="w-full"
          >
            {isComparing ? 'Comparing Predictions...' : 'Compare Prediction Methods'}
          </Button>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparisonResult && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Prediction (Suffix-based) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Current Prediction
              </CardTitle>
              <CardDescription>Suffix-based matching logic</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Prediction:</span>
                  <Badge variant={getPredictionBadgeVariant(comparisonResult.currentPrediction.prediction || 'UNCERTAIN')}>
                    {comparisonResult.currentPrediction.prediction || 'UNCERTAIN'}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Win Probability:</span>
                  <span className="text-sm font-semibold">
                    {comparisonResult.currentPrediction.winProbability?.toFixed(1) || '50.0'}%
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Confidence:</span>
                  <Badge variant={getConfidenceBadgeVariant(comparisonResult.currentPrediction.confidenceLevel || 'very_low')}>
                    {comparisonResult.currentPrediction.confidenceLevel || 'very_low'}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Patterns Used:</span>
                  <span className="text-sm font-semibold">
                    {comparisonResult.currentPrediction.matchingPatterns || 0}
                  </span>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Method Details:</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>• Uses full input sequence</div>
                  <div>• Strict array suffix matching</div>
                  <div>• Simple confidence calculation</div>
                  <div>• Up to 10 patterns with early stopping</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comparison Arrow */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <ArrowRight className="h-8 w-8 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium">VS</p>
                <p className="text-xs text-muted-foreground">Comparison</p>
              </div>
            </div>
          </div>

          {/* Honest Backtest Prediction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Honest Backtest Prediction
              </CardTitle>
              <CardDescription>makeRealisticPrediction logic</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Prediction:</span>
                  <Badge variant={getPredictionBadgeVariant(comparisonResult.honestBacktestPrediction.prediction)}>
                    {comparisonResult.honestBacktestPrediction.prediction}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Win Probability:</span>
                  <span className="text-sm font-semibold">
                    {comparisonResult.honestBacktestPrediction.winProbability.toFixed(1)}%
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Confidence:</span>
                  <Badge variant={getConfidenceBadgeVariant(comparisonResult.honestBacktestPrediction.confidence)}>
                    {comparisonResult.honestBacktestPrediction.confidence.toFixed(1)}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Patterns Used:</span>
                  <span className="text-sm font-semibold">
                    {comparisonResult.honestBacktestPrediction.usedPatterns}
                  </span>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Method Details:</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>• Uses last 8 trades only</div>
                  <div>• Flexible endsWith() matching</div>
                  <div>• Enhanced confidence formula</div>
                  <div>• Top 8 patterns, no early stopping</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Differences Summary */}
      {comparisonResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Differences Analysis
            </CardTitle>
            <CardDescription>
              Key differences between the two prediction methods
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium">Prediction Match</p>
                  <p className="text-xs text-muted-foreground">Same outcome?</p>
                </div>
                <div className="flex items-center gap-1">
                  {comparisonResult.differences.predictionMatch ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium">Win Probability Diff</p>
                  <p className="text-xs text-muted-foreground">Current - Honest</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">
                    {comparisonResult.differences.winProbabilityDiff > 0 ? '+' : ''}
                    {comparisonResult.differences.winProbabilityDiff.toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium">Confidence Diff</p>
                  <p className="text-xs text-muted-foreground">Current - Honest</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">
                    {comparisonResult.differences.confidenceDiff > 0 ? '+' : ''}
                    {comparisonResult.differences.confidenceDiff.toFixed(1)}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium">Patterns Diff</p>
                  <p className="text-xs text-muted-foreground">Current - Honest</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">
                    {comparisonResult.differences.patternsCountDiff > 0 ? '+' : ''}
                    {comparisonResult.differences.patternsCountDiff}
                  </p>
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <p className="text-sm font-medium">Analysis Summary:</p>
              <div className="text-sm text-muted-foreground space-y-1">
                {comparisonResult.differences.predictionMatch ? (
                  <div className="text-green-600">✓ Both methods predict the same outcome</div>
                ) : (
                  <div className="text-red-600">✗ Methods predict different outcomes - this explains accuracy differences</div>
                )}
                
                {Math.abs(comparisonResult.differences.winProbabilityDiff) > 10 && (
                  <div className="text-amber-600">
                    ⚠ Significant probability difference ({Math.abs(comparisonResult.differences.winProbabilityDiff).toFixed(1)}%) indicates different pattern matching logic
                  </div>
                )}
                
                {Math.abs(comparisonResult.differences.patternsCountDiff) > 3 && (
                  <div className="text-blue-600">
                    ℹ Different pattern counts indicate different selection criteria
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};